import { useEffect, useMemo, useRef, useState } from "react";
import Api from "../../Services/Api";
import { useAuth } from "../../auth/AuthContext";
import "./LicensedTutorialGate.css";

const VIDEO_SRC = "/tutorial-licenciado.MOV";
const TUTORIAL_STORAGE_PREFIX = "licensed_tutorial_completed_at";

function getTutorialStorageKey(user) {
  const userId = String(user?.id || "").trim();
  const userEmail = String(user?.email || "").trim().toLowerCase();
  const identity = userId || userEmail;

  if (!identity) return "";
  return `${TUTORIAL_STORAGE_PREFIX}:${identity}`;
}

function readStoredTutorialCompletion(user) {
  if (typeof window === "undefined") return "";

  const storageKey = getTutorialStorageKey(user);
  if (!storageKey) return "";

  try {
    return window.localStorage.getItem(storageKey) || "";
  } catch (_err) {
    return "";
  }
}

function storeTutorialCompletion(user, completedAt) {
  if (typeof window === "undefined") return;

  const storageKey = getTutorialStorageKey(user);
  if (!storageKey || !completedAt) return;

  try {
    window.localStorage.setItem(storageKey, String(completedAt));
  } catch (_err) {
    // Mantem o fluxo funcionando mesmo se o storage estiver indisponivel.
  }
}

function formatTime(totalSeconds) {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function hasCompletedTutorial(user) {
  return Boolean(
    user?.tutorial_completed ||
      user?.tutorial_completed_at ||
      readStoredTutorialCompletion(user)
  );
}

export default function LicensedTutorialGate({
  user,
  forceOpen = false,
  allowClose = false,
  onRequestClose,
}) {
  const { updateUser } = useAuth();
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const storedCompletedAt = readStoredTutorialCompletion(user);
  const tutorialCompleted = hasCompletedTutorial(user);
  const shouldBeOpen = forceOpen || !tutorialCompleted;
  const isReplayMode = forceOpen && tutorialCompleted;
  const [open, setOpen] = useState(() => shouldBeOpen);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [saveState, setSaveState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setOpen(shouldBeOpen);
    maxWatchedRef.current = 0;
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    setSaveState("idle");
    setErrorMessage("");
  }, [shouldBeOpen]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const video = videoRef.current;
    if (!video) return undefined;

    const timeoutId = window.setTimeout(() => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          setIsPlaying(false);
        });
      }
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const progress = useMemo(() => {
    if (!duration) return 0;
    return Math.min(100, Math.round((currentTime / duration) * 100));
  }, [currentTime, duration]);

  const togglePlayback = async () => {
    if (saveState === "saving") return;

    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      try {
        await video.play();
      } catch (_err) {
        setIsPlaying(false);
      }
      return;
    }

    video.pause();
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration || 0);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;

    const watchedTime = video.currentTime || 0;
    maxWatchedRef.current = Math.max(maxWatchedRef.current, watchedTime);
    setCurrentTime(watchedTime);
  };

  const handleSeeking = () => {
    const video = videoRef.current;
    if (!video) return;

    const allowedTime = maxWatchedRef.current + 0.25;
    if (video.currentTime > allowedTime) {
      video.currentTime = maxWatchedRef.current;
    }
  };

  const completeTutorial = async () => {
    if (tutorialCompleted) {
      setSaveState("done");
      return;
    }

    setSaveState("saving");
    setErrorMessage("");

    try {
      const response = await Api.post("/licensed/tutorial/complete");
      const completedAt =
        response?.data?.user?.tutorial_completed_at ||
        storedCompletedAt ||
        new Date().toISOString();

      storeTutorialCompletion(user, completedAt);

      updateUser({
        tutorial_completed: true,
        tutorial_completed_at: completedAt,
      });

      setSaveState("done");
      setOpen(false);
    } catch (err) {
      setSaveState("error");
      setErrorMessage(
        err?.response?.data?.message ||
          "O video terminou, mas nao conseguimos salvar a conclusao. Tente novamente."
      );
    }
  };

  const handleEnded = () => {
    setCurrentTime(duration || maxWatchedRef.current);
    maxWatchedRef.current = Math.max(maxWatchedRef.current, duration || 0);
    completeTutorial();
  };

  const handleClose = () => {
    if (!allowClose) return;
    setOpen(false);
    if (typeof onRequestClose === "function") {
      onRequestClose();
    }
  };

  if (!open) return null;

  return (
    <div className="licensed-tutorial-backdrop" role="dialog" aria-modal="true">
      <div className="licensed-tutorial-modal">
        <div className="licensed-tutorial-copy">
          <div className="licensed-tutorial-header">
            <div>
              <span className="licensed-tutorial-badge">
                {isReplayMode ? "Tutorial novamente" : "Tutorial inicial obrigatorio"}
              </span>
              <h2>
                {isReplayMode
                  ? "Assista novamente ao tutorial do sistema"
                  : "Assista ao video completo para liberar o sistema"}
              </h2>
            </div>

            {allowClose ? (
              <button
                type="button"
                className="licensed-tutorial-close"
                onClick={handleClose}
                aria-label="Fechar tutorial"
              >
                Fechar
              </button>
            ) : null}
          </div>

          <p>
            {isReplayMode
              ? "Use este video sempre que quiser revisar o passo a passo do sistema."
              : "Este tutorial aparece apenas no primeiro acesso do licenciado. O sistema sera liberado automaticamente quando o video chegar a 100%."}
          </p>
        </div>

        <div className="licensed-tutorial-player">
          <video
            ref={videoRef}
            className="licensed-tutorial-video"
            src={VIDEO_SRC}
            preload="auto"
            playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noplaybackrate noremoteplayback"
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleEnded}
            onContextMenu={(event) => event.preventDefault()}
          />

          <div className="licensed-tutorial-progress">
            <div className="licensed-tutorial-progress-bar">
              <span style={{ width: `${progress}%` }} />
            </div>
            <div className="licensed-tutorial-progress-meta">
              <strong>{progress}% concluido</strong>
              <span>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
          </div>

          <div className="licensed-tutorial-actions">
            <button
              type="button"
              className="licensed-tutorial-toggle"
              onClick={togglePlayback}
              disabled={saveState === "saving"}
            >
              {isPlaying ? "Pausar" : "Reproduzir"}
            </button>

            {saveState === "saving" ? (
              <span className="licensed-tutorial-status">Salvando conclusao...</span>
            ) : null}
          </div>

          {saveState === "error" ? (
            <div className="licensed-tutorial-error" role="alert">
              <p>{errorMessage}</p>
              <button type="button" onClick={completeTutorial}>
                Tentar concluir novamente
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
